import ProviderAPIClient from '@/features/auth/ProviderAPIClient'

type OrgClinician = {
  userId?: string
  userName?: string | null
  firstName?: string | null
  lastName?: string | null
  userEmail?: string | null
}

/** Bound display-name lookup so a hung directory request cannot block discard. */
export const DISPLAY_NAME_LOOKUP_TIMEOUT_MS = 3_000

let cliniciansCache: OrgClinician[] | null = null
let cliniciansCachePromise: Promise<OrgClinician[]> | null = null

async function loadOrgClinicians(): Promise<OrgClinician[]> {
  if (cliniciansCache) return cliniciansCache
  if (cliniciansCachePromise) return cliniciansCachePromise
  cliniciansCachePromise = (async () => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), DISPLAY_NAME_LOOKUP_TIMEOUT_MS)
    try {
      const client = ProviderAPIClient.getInstance()
      const res = await client.get('/auth/users/clinicians', undefined, true, controller.signal)
      if (!res.ok) return []
      const body = (await res.json()) as OrgClinician[]
      cliniciansCache = Array.isArray(body) ? body : []
      return cliniciansCache
    } catch {
      return []
    } finally {
      clearTimeout(timeoutId)
      cliniciansCachePromise = null
    }
  })()
  return cliniciansCachePromise
}

/** Best-effort org-member display name for draft-owner surfacing (§5.5). */
export async function resolveDraftOwnerDisplayName(ownerUserId: string | null | undefined): Promise<string | null> {
  const id = ownerUserId?.trim()
  if (!id) return null
  const clinicians = await loadOrgClinicians()
  const match = clinicians.find((c) => c.userId?.trim() === id)
  if (!match) return id
  const fromParts = `${match.firstName ?? ''} ${match.lastName ?? ''}`.trim()
  return match.userName?.trim() || fromParts || match.userEmail?.trim() || id
}

/** Test helper — reset cached clinicians between tests. */
export function resetDraftOwnerDisplayNameCache(): void {
  cliniciansCache = null
  cliniciansCachePromise = null
}
